// ImageOverlayEditor.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Typography, CircularProgress } from "@mui/material";
import { DeleteOutline, CloudUpload as CloudUploadIcon } from "@mui/icons-material";

// Types
import {
  ImageOverlay,
  ImageOverlayAnimation,
  ImageOverlayPosition,
} from "../types/MeGenTypes";

type ImageOverlayEditorProps = {
  overlays: ImageOverlay[];
  onChange: (overlays: ImageOverlay[]) => void;
  // upload: itemId = index ของ image overlay (ส่งเป็น string)
  onUploadFile?: (
    itemId: string,
    file: File,
    onSuccess: (url: string) => void,
    onError: () => void,
  ) => void;
  // สำหรับแปลง url ที่ backend คืนมาเป็น absolute url เพื่อ preview
  buildImageUrl?: (url: string) => string;
  disabled?: boolean;
};

// Consts
const POSITIONS: ImageOverlayPosition[] = [
  "top-left",
  "top-right",
  "bottom-left",
  "bottom-right",
  "center",
];

const ANIMATIONS: ImageOverlayAnimation[] = [
  "none",
  "fade",
  "slide-up",
  "slide-down",
  "slide-left",
  "slide-right",
];

const selectStyle: React.CSSProperties = {
  fontSize: 11,
  border: "1px solid #e2e8f0",
  borderRadius: 6,
  padding: "3px 6px",
  backgroundColor: "white",
  color: "#1e293b",
  cursor: "pointer",
  height: 26,
};

const numberInputStyle: React.CSSProperties = {
  width: 56,
  fontSize: 11,
  border: "1px solid #e2e8f0",
  borderRadius: 6,
  padding: "3px 6px",
  height: 26,
  textAlign: "center",
};

/**========================================================================
 **                            MAIN COMPONENT
 *========================================================================**/

const ImageOverlayEditor: React.FC<ImageOverlayEditorProps> = (props) => {
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const [localOverlays, setLocalOverlays] = useState<ImageOverlay[]>(props.overlays);
  useEffect(() => {
    setLocalOverlays(props.overlays);
  }, [props.overlays]);

  const updateOverlayDebounced = useCallback(
    (
      idx: number,
      patch: Partial<ImageOverlay>,
      overlaysSnapshot: ImageOverlay[],
      debounceMs = 400,
    ) => {
      const key = `overlay-${idx}-${Object.keys(patch).join(",")}`;
      if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key]);
      debounceTimers.current[key] = setTimeout(() => {
        const next = [...overlaysSnapshot];
        next[idx] = { ...next[idx], ...patch };
        props.onChange(next);
        delete debounceTimers.current[key];
      }, debounceMs);
    },
    [props.onChange],
  );

  const addOverlay = () => {
    const newOverlay: ImageOverlay = {
      id: Date.now().toString(),
      url: "",
      position: "bottom-right",
      animation: "none",
      start: 0,
      end: 0,
    };
    const next = [...localOverlays, newOverlay];
    setLocalOverlays(next);
    props.onChange(next);
  };

  const removeOverlay = (idx: number) => {
    if (!window.confirm("ต้องการลบ Image Overlay นี้หรือไม่?")) return;
    const next = localOverlays.filter((_, i) => i !== idx);
    setLocalOverlays(next);
    props.onChange(next);
  };

  const handleOverlayChange = (idx: number, patch: Partial<ImageOverlay>) => {
    const next = [...localOverlays];
    next[idx] = { ...next[idx], ...patch };
    setLocalOverlays(next);
    updateOverlayDebounced(idx, patch, localOverlays);
  };

  const handleOverlayImmediate = (idx: number, patch: Partial<ImageOverlay>) => {
    const next = [...localOverlays];
    next[idx] = { ...next[idx], ...patch };
    setLocalOverlays(next);
    props.onChange(next);
  };

  const handleSelectFile = (idx: number, overlay: ImageOverlay, file: File | null | undefined) => {
    if (!file || props.disabled) return;
    if (!file.type.startsWith("image/")) return;
    if (!props.onUploadFile) return;

    setUploadingId(overlay.id);
    props.onUploadFile(
      String(idx), // item_id = index ของ image overlay
      file,
      (url: string) => {
        setUploadingId(null);
        handleOverlayImmediate(idx, { url });
      },
      () => {
        setUploadingId(null);
      },
    );
  };

  const handleRemoveImage = (idx: number, overlay: ImageOverlay) => {
    if (!window.confirm("ต้องการลบรูปภาพนี้หรือไม่?")) return;
    const input = fileInputRefs.current[overlay.id];
    if (input) input.value = "";
    handleOverlayImmediate(idx, { url: "" });
  };

  return (
    <Box id="megen-image-overlay-root">
      {localOverlays.map((overlay, idx) => {
        const isUploading = uploadingId === overlay.id;
        const isDragging = draggingId === overlay.id;
        const previewUrl = overlay.url
          ? props.buildImageUrl
            ? props.buildImageUrl(overlay.url)
            : overlay.url
          : "";

        return (
          <Box
            id={`megen-image-overlay-item-${overlay.id}`}
            key={overlay.id}
            sx={{
              border: "1px solid #e8e8e8",
              borderRadius: 3,
              p: 2,
              pt: 1,
              mb: 2,
              backgroundColor: "white",
              position: "relative",
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            }}
          >
            {/* Delete overlay */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8", fontWeight: 500 }} />
              <button
                id={`megen-image-overlay-remove-btn-${overlay.id}`}
                onClick={() => removeOverlay(idx)}
                disabled={props.disabled || isUploading}
                style={{
                  background: "none",
                  border: "none",
                  cursor: props.disabled || isUploading ? "not-allowed" : "pointer",
                  color: "#ef4444",
                  padding: 4,
                  borderRadius: 6,
                  display: "flex",
                  alignItems: "center",
                  opacity: props.disabled || isUploading ? 0.5 : 1,
                }}
              >
                <DeleteOutline sx={{ fontSize: 20 }} />
              </button>
            </Box>

            {/* ── Toolbar: Position / Animation / Start / Duration ── */}
            <Box
              id={`megen-image-overlay-toolbar-${overlay.id}`}
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1,
                mb: 1.5,
                p: "8px 10px",
                background: "#f8fafc",
                borderRadius: 2,
                border: "1px solid #e2e8f0",
              }}
            >
              {/* Position */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Typography sx={{ fontSize: "0.68rem", color: "#64748b", whiteSpace: "nowrap" }}>
                  Position
                </Typography>
                <select
                  id={`megen-image-overlay-position-${overlay.id}`}
                  style={selectStyle}
                  value={overlay.position || "bottom-right"}
                  disabled={props.disabled}
                  onChange={(e) =>
                    handleOverlayImmediate(idx, {
                      position: e.target.value as ImageOverlayPosition,
                    })
                  }
                >
                  {POSITIONS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </Box>

              <div style={{ width: 1, height: 18, background: "#e2e8f0" }} />

              {/* Animation */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Typography sx={{ fontSize: "0.68rem", color: "#64748b", whiteSpace: "nowrap" }}>
                  Animation
                </Typography>
                <select
                  id={`megen-image-overlay-animation-${overlay.id}`}
                  style={selectStyle}
                  value={overlay.animation || "none"}
                  disabled={props.disabled}
                  onChange={(e) =>
                    handleOverlayImmediate(idx, {
                      animation: e.target.value as ImageOverlayAnimation,
                    })
                  }
                >
                  {ANIMATIONS.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </Box>

              <div style={{ width: 1, height: 18, background: "#e2e8f0" }} />

              {/* Start (s) */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Typography sx={{ fontSize: "0.68rem", color: "#64748b", whiteSpace: "nowrap" }}>
                  Start (s)
                </Typography>
                <input
                  id={`megen-image-overlay-start-${overlay.id}`}
                  type="number"
                  step={0.1}
                  min={0}
                  disabled={props.disabled}
                  value={overlay.start ?? 0}
                  style={numberInputStyle}
                  onChange={(e) =>
                    handleOverlayChange(idx, { start: parseFloat(e.target.value) || 0 })
                  }
                />
              </Box>

              {/* End (s) */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Typography sx={{ fontSize: "0.68rem", color: "#64748b", whiteSpace: "nowrap" }}>
                  End (s)
                </Typography>
                <input
                  id={`megen-image-overlay-end-${overlay.id}`}
                  type="number"
                  step={0.1}
                  min={0}
                  disabled={props.disabled}
                  value={overlay.end ?? 0}
                  style={numberInputStyle}
                  onChange={(e) =>
                    handleOverlayChange(idx, { end: parseFloat(e.target.value) || 0 })
                  }
                />
              </Box>

              <div style={{ width: 1, height: 18, background: "#e2e8f0" }} />

              {/* Scale (%) */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Typography sx={{ fontSize: "0.68rem", color: "#64748b", whiteSpace: "nowrap" }}>
                  Scale (%)
                </Typography>
                <input
                  id={`megen-image-overlay-scale-${overlay.id}`}
                  type="number"
                  step={5}
                  min={10}
                  max={90}
                  placeholder="Auto"
                  disabled={props.disabled}
                  value={overlay.scale ?? ""}
                  style={numberInputStyle}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const val = raw === "" ? undefined : parseFloat(raw);
                    handleOverlayChange(idx, { scale: val !== undefined && !isNaN(val) ? val : undefined });
                  }}
                />
              </Box>
            </Box>

            {/* ── Upload / Preview ── */}
            <Box>
              <Typography sx={{ fontSize: "0.72rem", color: "#64748b", mb: 0.5, fontWeight: 500 }}>
                Overlay Image
              </Typography>

              {/* hidden file input */}
              <input
                ref={(el) => {
                  fileInputRefs.current[overlay.id] = el;
                }}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => handleSelectFile(idx, overlay, e.target.files?.[0])}
              />

              {!overlay.url ? (
                <Box
                  id={`megen-image-overlay-dropzone-${overlay.id}`}
                  onClick={() => {
                    if (props.disabled || isUploading) return;
                    fileInputRefs.current[overlay.id]?.click();
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (!props.disabled && !isUploading) setDraggingId(overlay.id);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setDraggingId(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDraggingId(null);
                    if (props.disabled || isUploading) return;
                    handleSelectFile(idx, overlay, e.dataTransfer.files?.[0]);
                  }}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.75,
                    py: 3,
                    px: 2,
                    borderRadius: "0.5rem",
                    border: `1.5px dashed ${isDragging ? "#db2777" : "#f9a8d4"}`,
                    backgroundColor: isDragging ? "#fdf2f8" : "#fff5f9",
                    cursor: props.disabled || isUploading ? "not-allowed" : "pointer",
                    opacity: props.disabled ? 0.6 : 1,
                    transition: "all 0.15s",
                    "&:hover": { borderColor: "#db2777", backgroundColor: "#fdf2f8" },
                  }}
                >
                  {isUploading ? (
                    <>
                      <CircularProgress size={20} sx={{ color: "#db2777" }} />
                      <Typography sx={{ fontSize: "0.75rem", color: "#be185d", fontWeight: 500 }}>
                        Uploading...
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CloudUploadIcon sx={{ fontSize: 28, color: "#f9a8d4" }} />
                      <Typography sx={{ fontSize: "0.75rem", color: "#be185d", fontWeight: 500 }}>
                        ลากรูปมาวาง หรือคลิกเพื่อเลือกไฟล์
                      </Typography>
                      <Typography sx={{ fontSize: "0.625rem", color: "#94a3b8" }}>
                        รองรับไฟล์รูปภาพ (PNG, JPG, WEBP)
                      </Typography>
                    </>
                  )}
                </Box>
              ) : (
                /* Preview + actions */
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.25,
                    p: 1.25,
                    borderRadius: "0.5rem",
                    border: "0.5px solid #f9a8d4",
                    backgroundColor: "#fff5f9",
                  }}
                >
                  <Box
                    sx={{
                      width: 88,
                      height: 88,
                      borderRadius: "0.5rem",
                      overflow: "hidden",
                      flexShrink: 0,
                      border: "1.5px solid #f9a8d4",
                      backgroundColor: "#fce7f3",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {isUploading ? (
                      <CircularProgress size={20} sx={{ color: "#db2777" }} />
                    ) : (
                      <Box
                        component="img"
                        src={previewUrl}
                        alt="overlay preview"
                        sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                    )}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                    <Typography
                      sx={{
                        fontSize: "0.72rem",
                        fontWeight: 500,
                        color: "#9d174d",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {overlay.url.split("/").pop()}
                    </Typography>
                    <Typography sx={{ fontSize: "0.625rem", color: "#be185d", mb: "auto" }}>
                      {overlay.position} · {overlay.animation}
                    </Typography>

                    <Box sx={{ display: "flex", gap: 0.75 }}>
                      <Box
                        component="button"
                        id={`megen-image-overlay-change-btn-${overlay.id}`}
                        onClick={() => fileInputRefs.current[overlay.id]?.click()}
                        disabled={props.disabled || isUploading}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.375,
                          px: 1,
                          py: 0.5,
                          fontSize: "0.625rem",
                          fontWeight: 500,
                          color: "#be185d",
                          backgroundColor: "white",
                          border: "0.5px solid #f9a8d4",
                          borderRadius: "6px",
                          cursor: "pointer",
                          "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
                          "&:hover:not(:disabled)": { borderColor: "#db2777" },
                        }}
                      >
                        เปลี่ยนรูป
                      </Box>
                      <Box
                        component="button"
                        id={`megen-image-overlay-remove-image-btn-${overlay.id}`}
                        onClick={() => handleRemoveImage(idx, overlay)}
                        disabled={props.disabled || isUploading}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.375,
                          px: 1,
                          py: 0.5,
                          fontSize: "0.625rem",
                          fontWeight: 500,
                          color: "#dc2626",
                          backgroundColor: "white",
                          border: "0.5px solid #fecaca",
                          borderRadius: "6px",
                          cursor: "pointer",
                          "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
                          "&:hover:not(:disabled)": { backgroundColor: "#fef2f2" },
                        }}
                      >
                        <DeleteOutline sx={{ fontSize: 12 }} />
                        ลบรูป
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        );
      })}

      <Button
        id="megen-image-overlay-add-btn"
        fullWidth
        variant="outlined"
        size="small"
        onClick={addOverlay}
        disabled={props.disabled}
        sx={{
          borderColor: "#f9a8d4",
          color: "#db2777",
          fontSize: "0.72rem",
          "&:hover": { backgroundColor: "#fdf2f8", borderColor: "#be185d" },
        }}
      >
        + Add Image Overlay
      </Button>
    </Box>
  );
};

export default ImageOverlayEditor;